/**
 * UI-6b Task 6 — SlaGauge widget renderer.
 *
 * Fetches the last 24 hours of SLA data from /reports/sla?queueId=...
 * and renders the first row through the existing
 * `apps/web/src/modules/reports/charts/SlaGauge.tsx` component.
 *
 * Config: { queueId: string; targetPercent?: number }.
 *   Empty queueId → returns aggregate across all queues (backend behaviour).
 *
 * Fetches independently on mount + when queueId changes.
 */
import { useEffect, useState, type JSX } from 'react'
import { api } from '../../../../api/client.js'
import { SlaGauge } from '../../../../modules/reports/charts/SlaGauge.js'

export interface SlaGaugeConfig {
  queueId: string
  targetPercent?: number
}

interface SlaRow {
  queueId: string | null
  queueName: string | null
  slaPercent: number
  totalCalls: number
}

interface SlaResponse {
  rows: SlaRow[]
}

/** Returns ISO strings for [24h-ago, now]. */
function buildRange(): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to.getTime() - 24 * 60 * 60 * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}

export function SlaGaugeRenderer({ config }: { config: SlaGaugeConfig }): JSX.Element {
  const [row, setRow] = useState<SlaRow | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const { from, to } = buildRange()
    const query = new URLSearchParams({ from, to })
    if (config.queueId) query.set('queueId', config.queueId)

    api
      .get<SlaResponse>(`/reports/sla?${query.toString()}`)
      .then((resp) => {
        if (cancelled) return
        setRow(resp.rows[0] ?? null)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load SLA data')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return (): void => {
      cancelled = true
    }
  }, [config.queueId])

  if (loading) {
    return (
      <div data-testid="sla-gauge-widget-loading" className="text-sm text-gray-500">
        Loading SLA…
      </div>
    )
  }
  if (error) {
    return (
      <div data-testid="sla-gauge-widget-error" className="text-sm text-red-600">
        {error}
      </div>
    )
  }
  if (!row) {
    return (
      <div data-testid="sla-gauge-widget-empty" className="text-sm text-gray-500">
        No SLA data for the last 24 hours.
      </div>
    )
  }

  return (
    <div data-testid="sla-gauge-widget">
      {row.queueName ? (
        <div className="mb-1 text-xs text-gray-500">{row.queueName}</div>
      ) : null}
      <SlaGauge slaPercent={row.slaPercent} targetPercent={config.targetPercent ?? 90} />
    </div>
  )
}
